// Central project/case-study registry — single source of truth for the
// Work grid (`pages/Work.jsx`), homepage teaser (`components/Work.jsx`) and
// individual case study pages (`pages/CaseStudy.jsx`).
import { CLIENTS } from "@/lib/media";

export const PROJECTS = [
    {
        id: "hks",
        title: CLIENTS.hks.name,
        industry: "Manufacturing · Heritage",
        year: "2024",
        result: "150% ↑ Quality Leads",
        window: "6 months",
        img: CLIENTS.hks.banner,
        caseStudy: CLIENTS.hks.caseStudy,
        logo: CLIENTS.hks.logo,
        blurb:
            "A 190-year-old heritage manufacturer meeting a modern audience — brand refresh, digital presence and lead-gen campaigns tuned to a legacy story.",
        tags: ["Branding", "Performance", "Content"],
    },
    {
        id: "karnani",
        title: "Karnani · Ambika City",
        industry: "Real Estate · Residential",
        year: "2024",
        result: "200% ↑ Qualified Leads",
        window: "6 months",
        img: CLIENTS.karnani.banner,
        caseStudy: CLIENTS.karnani.caseStudy,
        logo: CLIENTS.karnani.logo,
        blurb:
            "A premium residential launch that needed to feel less like real estate and more like a lifestyle. Story, site, media — all in one voice.",
        tags: ["Branding", "Websites", "Performance"],
    },
    {
        id: "gv-ecostays",
        title: CLIENTS.gvEcostays.name,
        industry: "Hospitality · Eco-resort",
        year: "2024",
        result: "Full brand system",
        window: "Launch project",
        img: CLIENTS.gvEcostays.banner,
        logo: CLIENTS.gvEcostays.logo,
        blurb:
            "Ground-up brand identity for a hillside eco-stay — a warm, editorial voice built around 'stay, sustain, connect' and rolled across every touchpoint.",
        tags: ["Branding", "Websites", "Content"],
    },
    {
        id: "sudama-farms",
        title: CLIENTS.sudamaFarms.name,
        industry: "Agriculture · D2C",
        year: "2024",
        result: "Farm-to-fork identity",
        window: "Launch project",
        img: CLIENTS.sudamaFarms.banner,
        logo: CLIENTS.sudamaFarms.logo,
        blurb:
            "Organic produce brand built for a modern kitchen — warm, honest and unmistakably rooted. Identity, packaging and always-on content.",
        tags: ["Branding", "Content", "Commerce"],
    },
    {
        id: "logoinfosoft",
        title: CLIENTS.logoinfosoft.name,
        industry: "Technology · SaaS",
        year: "2025",
        result: "300% ↑ Qualified Leads",
        window: "6 months",
        img: CLIENTS.logoinfosoft.caseStudy,
        caseStudy: CLIENTS.logoinfosoft.caseStudy,
        logo: CLIENTS.logoinfosoft.logo,
        blurb:
            "Performance marketing engine for a B2B SaaS product suite — sharp audience segmentation, creative iteration and an attribution model that survived the funnel.",
        tags: ["Performance", "Websites", "Content"],
    },
    {
        id: "scf",
        title: CLIENTS.scf.name,
        industry: "Non-profit · Mobility",
        year: "2024",
        result: "3× Engagement",
        window: "Across campaigns",
        img: CLIENTS.scf.caseStudy,
        caseStudy: CLIENTS.scf.caseStudy,
        logo: CLIENTS.scf.logo,
        blurb:
            "A cause-driven brand advocating for safer, kinder cities — editorial creative, awareness campaigns and a message that moved from feed to street.",
        tags: ["Branding", "Content", "Performance"],
    },
    {
        id: "sbca",
        title: "School of Bakery & Culinary Art",
        industry: "Education · F&B",
        year: "2024",
        result: "200% ↑ Enrolments",
        window: "6 months",
        img: CLIENTS.sbca.caseStudy,
        caseStudy: CLIENTS.sbca.caseStudy,
        logo: null,
        blurb:
            "A specialist culinary school with a distinctive point of view — brand storytelling, admissions campaigns and content that made courses feel worth waiting for.",
        tags: ["Branding", "Performance", "Content"],
    },
];

export function getProjectBySlug(slug) {
    return PROJECTS.find((p) => p.id === slug);
}

// Returns the previous/next project relative to `slug`, wrapping around.
export function getAdjacentProjects(slug) {
    const i = PROJECTS.findIndex((p) => p.id === slug);
    if (i === -1) return { prev: null, next: null };
    const prev = PROJECTS[(i - 1 + PROJECTS.length) % PROJECTS.length];
    const next = PROJECTS[(i + 1) % PROJECTS.length];
    return { prev, next };
}

// Returns up to `count` other projects ranked by number of shared tags
// (most similar first). Ties keep the registry's natural order. Backfills
// with remaining projects if there aren't enough tag matches, so callers
// always get `count` results (as long as enough projects exist).
export function getRelatedProjects(slug, count = 3) {
    const current = getProjectBySlug(slug);
    if (!current) return [];

    return PROJECTS.filter((p) => p.id !== slug)
        .map((p) => ({
            project: p,
            shared: p.tags.filter((t) => current.tags.includes(t)).length,
        }))
        .sort((a, b) => b.shared - a.shared)
        .slice(0, count)
        .map((entry) => entry.project);
}
